import React, { useMemo } from 'react';
import { layer, source, feature } from 'react-openlayers';
import { fromLonLat } from 'ol/proj';
import type { Feature } from 'geojson';

interface Props {
  defectiveGeometries?: Feature<GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString>[];
  warningGeometries?: Feature<GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString>[];
}

/**
 * Layer to display defective geometries on the map with bright red highlighting
 * and dashed circles around error points
 */
export function DefectiveGeometriesLayer({ defectiveGeometries, warningGeometries }: Props) {
  const errorFeatures = useMemo(() => {
    if (!defectiveGeometries || defectiveGeometries.length === 0) {
      return [];
    }

    const features: React.JSX.Element[] = [];

    defectiveGeometries.forEach((defectiveGeometry, index) => {
      if (defectiveGeometry.geometry.type !== 'Point') {
        return;
      }

      const coords = defectiveGeometry.geometry.coordinates as [number, number];
      const errorCoordinate = fromLonLat(coords);

      // Bright red point marker with error label
      features.push(
        <feature.PointReact
          key={`error-point-${index}`}
          coordinate={errorCoordinate}
          circleOptions={{
            radius: 8,
            fillOptions: { color: 'rgba(255, 0, 0, 1)' },
            strokeOptions: { color: 'rgba(255, 255, 255, 1)', width: 2 },
          }}
          textOptions={{
            text: defectiveGeometry.properties?.error || '',
            font: 'bold 14px Calibri,sans-serif',
            fillOptions: { color: '#fff' },
            strokeOptions: { color: 'rgba(180, 0, 0, 1)', width: 4 },
          }}
        />,
      );
    });

    return features;
  }, [defectiveGeometries]);

  const warningFeatures = useMemo(() => {
    if (!warningGeometries || warningGeometries.length === 0) {
      return [];
    }

    const features: React.JSX.Element[] = [];

    warningGeometries.forEach((warningGeometry, index) => {
      if (warningGeometry.geometry.type === 'Point') {
        const coords = warningGeometry.geometry.coordinates as [number, number];
        const warningCoordinate = fromLonLat(coords);

        features.push(
          <feature.PointReact
            key={`warning-point-${index}`}
            coordinate={warningCoordinate}
            circleOptions={{
              radius: 8,
              fillOptions: { color: 'rgba(255, 165, 0, 1)' },
              strokeOptions: { color: 'rgba(255, 255, 255, 1)', width: 2 },
            }}
            textOptions={{
              text: warningGeometry.properties?.error || '',
              font: 'bold 14px Calibri,sans-serif',
              fillOptions: { color: '#fff' },
              strokeOptions: { color: 'rgba(200, 120, 0, 1)', width: 4 },
            }}
          />,
        );

        return;
      }

      if (warningGeometry.geometry.type === 'LineString') {
        const lineCoords = (warningGeometry.geometry.coordinates as [number, number][]).map((c) => fromLonLat(c));

        features.push(
          <feature.LineStringReact
            key={`warning-line-${index}`}
            coordinates={lineCoords}
            strokeOptions={{
              color: 'rgba(255, 0, 0, 1)',
              width: 3,
              lineDash: [6, 4],
            }}
          />,
        );

        return;
      }
    });

    return features;
  }, [warningGeometries]);

  const hasErrors = defectiveGeometries && defectiveGeometries.length > 0 && errorFeatures.length > 0;
  const hasWarnings = warningGeometries && warningGeometries.length > 0 && warningFeatures.length > 0;

  if (!hasErrors && !hasWarnings) {
    return null;
  }

  return (
    // @ts-ignore
    <layer.Vector zIndex={1000}>
      {/* @ts-ignore */}
      <source.VectorSourceReact>
        {errorFeatures}
        {warningFeatures}
      </source.VectorSourceReact>
    </layer.Vector>
  );
}
